import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/esm/extends';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import React from 'react';
import { Box } from '@trybeapp/ui.box';
import { cardStyles } from '@trybeapp/ui.utilities';
import { ClearButton } from '@trybeapp/ui.clear-button';
import { useDialogState, DialogDisclosure, DialogBackdrop, Dialog } from 'reakit/Dialog';
import { Shape } from '@trybeapp/ui.shape';
import styled, { css, th } from '@xstyled/styled-components';
import { system } from '@trybeapp/ui.system';

var _excluded = ["ariaLabel", "children", "hide", "hideOnClickOutside", "onClose", "closeElement", "size"];
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
var Close = /*#__PURE__*/styled(ClearButton)(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  z-index: 1;\n  right: ", ";\n  top: ", ";\n"])), /*#__PURE__*/th('space.sm'), /*#__PURE__*/th('space.sm'));
var StyledBackdrop = /*#__PURE__*/styled(DialogBackdrop).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['hideOnClickOutside'].includes(prop);
  }
})(function (_ref) {
  var hideOnClickOutside = _ref.hideOnClickOutside;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    ", ";\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    position: fixed;\n    top: 0;\n    right: 0;\n    left: 0;\n    bottom: 0;\n    opacity: 0;\n    transition: opacity 150ms ease-in-out;\n    ", "\n\n    /* on open dialog for animation */\n    &[data-enter] {\n      opacity: 1;\n    }\n  "])), th('modals.backdrop'), hideOnClickOutside && css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n        cursor: pointer;\n      "]))));
});
var StyledDialog = /*#__PURE__*/styled(Dialog)(function (_ref2) {
  var size = _ref2.size,
    theme = _ref2.theme;
  return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n    ", ";\n    ", ";\n    ", ";\n    position: relative;\n    display: flex;\n    flex-direction: column;\n    margin-top: 20;\n    opacity: 0;\n    max-height: calc(100% - ", ");\n    max-width: calc(100% - ", ");\n    transition: opacity 250ms ease-in-out, margin-top 250ms ease-in-out;\n    cursor: auto;\n\n    &:focus {\n      outline: none;\n    }\n\n     /* on open dialog for animation */\n     &[data-enter] {\n      opacity: 1;\n      margin-top: 0;\n    }\n  "])), cardStyles, th('modals.default'), th("modals.sizes." + size), theme.toRem(20), theme.toRem(20));
});
var StyledContent = /*#__PURE__*/styled(Box)(_templateObject5 || (_templateObject5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n  padding: xxl;\n  color: dark.900;\n  flex: 1 1 0%;\n"])), system);
var StyledCover = /*#__PURE__*/styled(Shape)(_templateObject6 || (_templateObject6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", ";\n"])), /*#__PURE__*/th('modals.cover'));
var StyledHeader = /*#__PURE__*/styled(Box)(_templateObject7 || (_templateObject7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", ";\n  width: 100%;\n  padding: lg xxl;\n"])), /*#__PURE__*/th('modals.header'));
var StyledFooter = /*#__PURE__*/styled(Box)(_templateObject8 || (_templateObject8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", ";\n  width: 100%;\n  padding: lg xxl;\n"])), /*#__PURE__*/th('modals.footer'));
function useModalState(options) {
  return useDialogState(_extends({
    animated: true
  }, options));
}
function Modal(_ref3) {
  var ariaLabel = _ref3.ariaLabel,
    children = _ref3.children,
    hide = _ref3.hide,
    _ref3$hideOnClickOuts = _ref3.hideOnClickOutside,
    hideOnClickOutside = _ref3$hideOnClickOuts === void 0 ? true : _ref3$hideOnClickOuts,
    onClose = _ref3.onClose,
    _ref3$closeElement = _ref3.closeElement,
    CloseElement = _ref3$closeElement === void 0 ? Close : _ref3$closeElement,
    _ref3$size = _ref3.size,
    size = _ref3$size === void 0 ? 'lg' : _ref3$size,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded);
  var closeModal = function closeModal() {
    if (onClose) onClose();
    hide();
  };
  return /*#__PURE__*/React.createElement(StyledBackdrop, _extends({}, rest, {
    hideOnClickOutside: hideOnClickOutside
  }), /*#__PURE__*/React.createElement(StyledDialog, _extends({
    "aria-label": ariaLabel,
    hide: closeModal,
    hideOnClickOutside: hideOnClickOutside,
    size: size
  }, rest), /*#__PURE__*/React.createElement(CloseElement, {
    onClick: closeModal,
    size: "sm",
    variant: "tertiary"
  }), children));
}
Modal.Trigger = DialogDisclosure;
Modal.Content = StyledContent;
Modal.Footer = StyledFooter;
Modal.Cover = StyledCover;
Modal.Header = StyledHeader;

export { Modal, useModalState };
